// API Configuration for TRAQ Manager

// Base URL for the API - update this to match your backend server
// For development: use localhost
// For production: use the deployed URL
const isDevelopment = false;// !process.env.NODE_ENV || process.env.NODE_ENV === 'development';
// const isDevelopment = true;
export const API_BASE_URL = isDevelopment 
  ? 'http://127.0.0.1:5005/traq-e3acf/us-central1/app/managers'
  : 'https://us-central1-traq-e3acf.cloudfunctions.net/app/managers';

// API Endpoints
export const API_ENDPOINTS = {
  // Manager Authentication
  LOGIN: '/login',
  SIGNUP: '/signup',
  HEARTBEAT: '/heartBeat',
  
  // Manager Profile
  UPDATE_PROFILE: '/profile',
  UPDATE_PASSWORD: '/password',
  
  // Staff Management
  GET_STAFF: '/staff',
  CREATE_STAFF: '/staff',
  GET_STAFF_DETAILS: (staffId) => `/staff/${staffId}`,
  UPDATE_STAFF_STATUS: (staffId) => `/staff/${staffId}/status`,
  GET_STAFF_ATTENDANCE: (staffId) => `/staff/${staffId}/attendance`,
  GET_STAFF_SALES: (staffId) => `/staff/${staffId}/sales`,
  UPDATE_SALE: (staffId, saleId) => `/staff/${staffId}/sales/${saleId}`,
  GET_STAFF_CUSTOMERS: (staffId) => `/staff/${staffId}/customers`,
  GET_STAFF_CHANGE_LOGS: (staffId) => `/staff/${staffId}/change-logs`,
  GET_STAFF_TODAY_OVERVIEW: (staffId) => `/staff/${staffId}/today-overview`,
  ASSIGN_STAFF: (staffId) => `/assign-staff/${staffId}`,
  UNASSIGN_STAFF: (staffId) => `/unassign-staff/${staffId}`,
  
  // Sales Management
  GET_SALES: '/sales',
  
  // Dashboard
  GET_TODAY_SNAPSHOT: '/today-snapshot',
  
  // Leaderboard
  GENERATE_LEADERBOARD_TOKEN: '/leaderboard/generate-token',
  GET_LEADERBOARD_DATA: (token) => `/leaderboard/${token}`,
  
  // Commissions / Productivity Sheets
  GET_COMMISSIONS: (staffId) => `/commissions/${staffId}`,
  CREATE_COMMISSION: (staffId) => `/commissions/${staffId}`,
  UPDATE_COMMISSION: (commissionId) => `/commissions/${commissionId}`,
  DELETE_COMMISSION: (commissionId) => `/commissions/${commissionId}`,
  GET_PRODUCTIVITY_SHEET: (commissionId) => `/productivity-sheet/${commissionId}`,
  GENERATE_PRODUCTIVITY_SHEET_TOKEN: (commissionId) => `/productivity-sheet/${commissionId}/generate-token`,
  GET_PUBLIC_PRODUCTIVITY_SHEET: (token) => `/productivity-sheet/view/${token}`,
  
  // Device Management (Read-only for managers)
  GET_DEVICES: '/devices',
};

// HTTP Headers
export const getAuthHeaders = () => {
  const token = localStorage.getItem('traq_access_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// API Helper Functions
export const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const config = {
    headers: getAuthHeaders(),
    ...options
  };

  try {
    console.log(`API Request: ${config.method || 'GET'} ${url}`);
    const response = await fetch(url, config);
    
    // Handle authentication errors
    if (response.status === 401) {
      // Clear stored tokens and throw error (let component handle logout)
      localStorage.removeItem('traq_access_token');
      localStorage.removeItem('traq_user_data');
      throw new Error('Authentication failed');
    }

    // Try to parse as JSON, but handle cases where response is not JSON
    let data;
    const contentType = response.headers.get('content-type');
    console.log(`Response status: ${response.status}, Content-Type: ${contentType}`);
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      console.log('Response text:', text.substring(0, 200)); // Log first 200 chars
      
      // Check if it's HTML error page
      if (text.includes('<!DOCTYPE html>') || text.includes('<html')) {
        throw new Error(`Server returned HTML error page. Status: ${response.status}. Check backend logs.`);
      }
      
      // If response is not JSON but looks like an object, try parsing
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
    }
    
    if (!response.ok) {
      const errorMessage = typeof data === 'object' ? (data.message || data.error) : data;
      throw new Error(errorMessage || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    console.error('API Request failed:', error);
    throw error;
  }
};

// Specific API functions
export const managerAPI = {
  // Login manager
  login: async (email, passcode) => {
    return apiRequest(API_ENDPOINTS.LOGIN, {
      method: 'POST',
      body: JSON.stringify({ email, passcode })
    });
  },

  // Signup manager
  signup: async (first_name, last_name, email, passcode, organisationID) => {
    return apiRequest(API_ENDPOINTS.SIGNUP, {
      method: 'POST',
      body: JSON.stringify({ first_name, last_name, email, passcode, organisationID })
    });
  },

  // Send heartbeat
  heartbeat: async () => {
    return apiRequest(API_ENDPOINTS.HEARTBEAT, {
      method: 'PUT'
    });
  },

  // Get allocated staff
  getStaff: async () => {
    return apiRequest(API_ENDPOINTS.GET_STAFF);
  },

  // Create new staff member
  createStaff: async (staffData) => {
    return apiRequest(API_ENDPOINTS.CREATE_STAFF, {
      method: 'POST',
      body: JSON.stringify(staffData)
    });
  },

  // Get staff details
  getStaffDetails: async (staffId) => {
    return apiRequest(API_ENDPOINTS.GET_STAFF_DETAILS(staffId));
  },

  updateStaffStatus: async (staffId, status) => {
    return apiRequest(API_ENDPOINTS.UPDATE_STAFF_STATUS(staffId), {
      method: 'PUT',
      body: JSON.stringify({ status })
    });
  },

  allocateStaff: async (staffId) => {
    return apiRequest(API_ENDPOINTS.ASSIGN_STAFF(staffId), {
      method: 'POST'
    });
  },

  deallocateStaff: async (staffId) => {
    return apiRequest(API_ENDPOINTS.UNASSIGN_STAFF(staffId), {
      method: 'DELETE'
    });
  },

  // Get sales data
  getSales: async () => {
    return apiRequest(API_ENDPOINTS.GET_SALES);
  },

  // Get today's snapshot (attendance + sales)
  getTodaySnapshot: async () => {
    return apiRequest(API_ENDPOINTS.GET_TODAY_SNAPSHOT);
  },

  // Get today's overview for a specific staff member
  getStaffTodayOverview: async (staffId) => {
    return apiRequest(API_ENDPOINTS.GET_STAFF_TODAY_OVERVIEW(staffId));
  },

  // Update manager profile
  updateProfile: async (profileData) => {
    return apiRequest(API_ENDPOINTS.UPDATE_PROFILE, {
      method: 'PUT',
      body: JSON.stringify(profileData)
    });
  },

  // Update manager password
  updatePassword: async (currentPasscode, newPasscode) => {
    return apiRequest(API_ENDPOINTS.UPDATE_PASSWORD, {
      method: 'PUT',
      body: JSON.stringify({ currentPasscode, newPasscode })
    });
  },

  // Generate leaderboard token
  generateLeaderboardToken: async () => {
    return apiRequest(API_ENDPOINTS.GENERATE_LEADERBOARD_TOKEN, {
      method: 'POST'
    });
  },

  // Get leaderboard data (public endpoint, no auth)
  getLeaderboardData: async (token) => {
    const url = `${API_BASE_URL}${API_ENDPOINTS.GET_LEADERBOARD_DATA(token)}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error('Failed to fetch leaderboard data');
    }
    
    return response.json();
  },

  // Get commissions for staff
  getCommissions: async (staffId) => {
    return apiRequest(API_ENDPOINTS.GET_COMMISSIONS(staffId));
  },

  // Create new commission
  createCommission: async (staffId, commissionData) => {
    return apiRequest(API_ENDPOINTS.CREATE_COMMISSION(staffId), {
      method: 'POST',
      body: JSON.stringify(commissionData)
    });
  },

  // Update commission
  updateCommission: async (commissionId, updateData) => {
    return apiRequest(API_ENDPOINTS.UPDATE_COMMISSION(commissionId), {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  },

  // Delete commission
  deleteCommission: async (commissionId) => {
    return apiRequest(API_ENDPOINTS.DELETE_COMMISSION(commissionId), {
      method: 'DELETE'
    });
  },

  // Get productivity sheet data
  getProductivitySheet: async (commissionId) => {
    return apiRequest(API_ENDPOINTS.GET_PRODUCTIVITY_SHEET(commissionId));
  },

  // Generate shareable token for productivity sheet
  generateProductivitySheetToken: async (commissionId) => {
    return apiRequest(API_ENDPOINTS.GENERATE_PRODUCTIVITY_SHEET_TOKEN(commissionId), {
      method: 'POST'
    });
  },

  // Get public productivity sheet (no auth required)
  getPublicProductivitySheet: async (token) => {
    const url = `${API_BASE_URL}${API_ENDPOINTS.GET_PUBLIC_PRODUCTIVITY_SHEET(token)}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error('Failed to fetch productivity sheet');
    }
    
    return response.json();
  },

  // Get all devices (read-only)
  getDevices: async () => {
    return apiRequest(API_ENDPOINTS.GET_DEVICES);
  }
};

// Staff-specific API functions
export const staffAPI = {
  // Get staff attendance
  getStaffAttendance: async (staffId, params) => {
    const queryParams = new URLSearchParams();
    if (params) {
      if (params.startDate && params.endDate) {
        queryParams.append('startDate', params.startDate);
        queryParams.append('endDate', params.endDate);
      } else if (params.days) {
        queryParams.append('days', params.days);
      } else if (params.from && params.till) {
        // Convert old format to new format
        queryParams.append('startDate', params.from.replace('_', ''));
        queryParams.append('endDate', params.till.replace('_', ''));
      }
    }
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return apiRequest(API_ENDPOINTS.GET_STAFF_ATTENDANCE(staffId) + query);
  },

  // Get staff sales
  getStaffSales: async (staffId, params) => {
    const queryParams = new URLSearchParams();
    if (params) {
      if (params.startDate && params.endDate) {
        queryParams.append('startDate', params.startDate);
        queryParams.append('endDate', params.endDate);
      } else if (params.days) {
        queryParams.append('days', params.days);
      } else if (params.from && params.till) {
        // Convert old format to new format
        queryParams.append('startDate', params.from.replace('_', ''));
        queryParams.append('endDate', params.till.replace('_', ''));
      }
    }
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return apiRequest(API_ENDPOINTS.GET_STAFF_SALES(staffId) + query);
  },

  // Update a sale
  updateSale: async (staffId, saleId, updateData) => {
    return apiRequest(API_ENDPOINTS.UPDATE_SALE(staffId, saleId), {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  },

  // Get staff customers
  getStaffCustomers: async (staffId) => {
    return apiRequest(API_ENDPOINTS.GET_STAFF_CUSTOMERS(staffId));
  },

  // Get staff change logs
  getStaffChangeLogs: async (staffId, params) => {
    const queryParams = new URLSearchParams();
    if (params) {
      if (params.startDate && params.endDate) {
        queryParams.append('startDate', params.startDate);
        queryParams.append('endDate', params.endDate);
      } else if (params.days) {
        queryParams.append('days', params.days);
      } else if (params.from && params.till) {
        // Convert old format to new format
        queryParams.append('startDate', params.from.replace('_', ''));
        queryParams.append('endDate', params.till.replace('_', ''));
      }
    }
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return apiRequest(API_ENDPOINTS.GET_STAFF_CHANGE_LOGS(staffId) + query);
  },
};

export default managerAPI;
